import { apolloClient } from "@/graphql/client";
import { StudioNotebookDocument, UpdateStudioNotebookDocument } from "@/graphql/generated/operations";
import { createAssistantAgent, runAguiTurn } from "@/lib/agent-agui";
import { GROW_NOTE_MAX_ITEMS, parseGrowNote, type GrowNoteCount, type GrowNoteParts } from "@/lib/grow-note";
import type { AssistantContextItem } from "@/lib/use-assistant-context";
import { useGrowNoteRuns } from "@/stores/grow-note-store";

// The note request of the Grow save chip, or of a save the person typed (spark#1218, "Ablauf beim
// Speichern"): one GROWTH run that writes the note's own parts into the note the save has just created and
// opened (@adr SPARK-0060-a-saved-grow-note-is-first-written-as-its-own-fallback@1). It runs on its own AG-UI agent
// and touches nothing of the drawer's conversation; Studio shows it inside the note from useGrowNoteRuns.
// The created note already is the fallback, so only a well-formed answer leads to a write. agent-service
// keeps nothing of the run and lets nothing stop it
// (@external-adr AGENT-SERVICE-0046-a-grow-note-request-keeps-nothing).

/**
 * How long the request may take to its first text. It searches the reference work and writes a long answer
 * before anything streams, so this is far beyond a chat turn's wait; after the first text the turn's own idle
 * watchdog applies.
 */
export const GROW_NOTE_FIRST_TEXT_MS = 180_000;

export interface GrowNoteJob {
  notebookId: string;
  /** The note as the save created it: what the completion replaces. */
  initial: string;
  /** The note's line that its own parts could not be written. */
  line: string;
  message: string;
  context: AssistantContextItem[];
  /** How many examples, each with its goal, the note holds; the chip's three when absent (spark#1218). */
  items?: GrowNoteCount;
  /** The person's own wishes for this note, when they asked for it in their own words (`growNoteWishes`). */
  wishes?: string;
  /** The complete note from the parts the request wrote. */
  compose: (parts: GrowNoteParts) => string;
}

/**
 * The completed note written to genesis, when genesis still holds the created note: unchanged, or with text
 * added around it, which is kept. Content only, so a rename made meanwhile stays. Null when nothing was
 * written: the note changed otherwise or is gone, or the update failed twice.
 */
async function writeCompleted(job: GrowNoteJob, completed: string): Promise<string | null> {
  const { data } = await apolloClient.query({
    query: StudioNotebookDocument,
    variables: { id: job.notebookId },
    fetchPolicy: "network-only",
  });
  const current = data?.studioNotebook?.content;
  if (current == null || !current.includes(job.initial)) return null;
  // A function, so a `$` in the note is not read as a replacement pattern.
  const content = current === job.initial ? completed : current.replace(job.initial, () => completed);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data: updated } = await apolloClient.mutate({
        mutation: UpdateStudioNotebookDocument,
        variables: { id: job.notebookId, input: { data: { content } } },
      });
      const out = updated?.updateStudioNotebook;
      if (out?.__typename === "UpdateStudioNotebookResult") return out.result.content ?? content;
    } catch {
      // Tried once more below; after that the note keeps what it has.
    }
  }
  return null;
}

/** Starts the note request for a note the save created. Never throws; the run reports through useGrowNoteRuns. */
export function startGrowNoteRun(job: GrowNoteJob): void {
  const abortController = new AbortController();
  let timedOut = false;
  let failed = false;
  let text = "";
  const items = job.items ?? GROW_NOTE_MAX_ITEMS;
  // At once, so Studio holds the note still from the first time it shows it.
  useGrowNoteRuns.getState().begin(job.notebookId, job.line);
  // The way out when no text comes in time: the stream ends here, and the run on the server ends at its own
  // cap. Nothing else ends it early: whether the note gets a Stop of its own is still open in spark#1218.
  const firstText = setTimeout(() => {
    timedOut = true;
    abortController.abort();
  }, GROW_NOTE_FIRST_TEXT_MS);

  void (async () => {
    let written: string | null = null;
    let reason = "";
    try {
      await runAguiTurn(
        createAssistantAgent(),
        {
          message: job.message,
          section: "growth",
          threadId: null,
          uiContext: job.context,
          action: "GENERATE_GROWTH_NOTE",
          // Only a typed save sends it: the chip's request (three examples, no wishes) stays as it was.
          ...(job.wishes !== undefined || items !== GROW_NOTE_MAX_ITEMS
            ? { growNote: { items, ...(job.wishes !== undefined ? { wishes: job.wishes } : {}) } }
            : {}),
          searchSources: [],
          abortController,
        },
        {
          onDelta: (delta) => {
            clearTimeout(firstText);
            text += delta;
            useGrowNoteRuns.getState().append(job.notebookId, delta);
          },
          // A finished note comes with no notice, so any notice means the answer is not one.
          onNotice: () => {
            failed = true;
          },
          onError: () => {
            failed = true;
          },
          onRunId: () => {},
          onThreadId: () => {},
          onToolCall: () => {},
          onToolResult: () => {},
          onSuggestions: () => {},
          onProgress: () => {},
          onRunResult: () => {},
        },
      );
      const parts = timedOut || failed ? null : parseGrowNote(text, items);
      if (parts) {
        written = await writeCompleted(job, job.compose(parts));
        if (written === null) reason = "the note changed meanwhile, or the update failed";
      } else {
        reason = timedOut ? "no text in time" : failed ? "failed" : "not a note";
      }
    } catch {
      reason = timedOut ? "no text in time" : "failed";
    } finally {
      clearTimeout(firstText);
      useGrowNoteRuns.getState().finish(job.notebookId, written === null ? undefined : { text: written, initial: job.initial });
      // Why the note kept its line, never what it says.
      if (written === null) console.debug("[grow-note] not completed:", reason);
    }
  })();
}
